import React from "react";
import { NavLink } from "react-router-dom";
import {
  Boxes, Building2, Home, Inbox, LayoutList, LifeBuoy, PieChart, Receipt, Warehouse,
} from "lucide-react";

/**
 * The store's own shell: one nav down the side, the section on the right.
 *
 * The module had grown to seven screens reached through the portal's main
 * sidebar, which buried them among everything else the Institute does. A store
 * is worked as a place — you stand in it and move between its jobs — so it
 * gets its own frame, and the portal's sidebar needs only one entry into it.
 *
 * There is no Orders section. The Institute's store sells across the counter
 * and issues to staff; it does not take orders to fulfil later, and a section
 * that could never be anything but empty is worse than its absence.
 *
 * Customers and Suppliers are out for now too. Both were derived from the
 * movements — who a sale recorded as buying, who a receipt recorded as
 * supplying — and the API can answer either question again the moment they are
 * wanted back.
 */

const NAV = [
  { to: "/stores", end: true, label: "Home", icon: Home },
  { to: "/stores/sales", label: "Sales", icon: Receipt },
  { to: "/stores/items", label: "Products", icon: LayoutList },
  { to: "/stores/inventory", label: "Inventory", icon: Boxes },
  // What a branch asks the main store for, and what it suggests the register
  // ought to carry — the only way a custodian can act beyond their own shelves.
  { to: "/stores/requests", label: "Requests", icon: Inbox },
  { to: "/stores/reports", label: "Reports", icon: PieChart },
  { to: "/stores/staff", label: "Staff", icon: Warehouse },
  { to: "/stores/settings", label: "Settings", icon: Building2 },
];

export default function StoreShell({
  title,
  subtitle,
  actions,
  children,
}: {
  /** Omitted by screens that already carry their own header. */
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Nav. Horizontal and scrollable on a phone, a column on a desk. */}
        <nav className="lg:w-56 shrink-0">
          <div className="bg-white border rounded-xl p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {NAV.map(({ to, end, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-gray-900 text-white font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </NavLink>
            ))}

            <div className="hidden lg:block border-t mt-2 pt-2">
              <NavLink
                to="/support/tickets/new"
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-50"
              >
                <LifeBuoy className="w-4 h-4 shrink-0" />
                Help and support
              </NavLink>
            </div>
          </div>
        </nav>

        <div className="flex-1 min-w-0 space-y-5">
          {(title || actions) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                {title && <h1 className="text-xl font-bold text-gray-800">{title}</h1>}
                {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
              </div>
              {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}
